import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/app/Navbar";
import Footer from "@/components/app/Footer";
import { api, fmtINR, imageUrl } from "@/lib/api";
import { useAuth, startGoogleLogin } from "@/lib/auth";
import { Plus, Pencil, Trash2, Loader2, BookOpen, Bike } from "lucide-react";
import { toast } from "sonner";

export default function HostDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [listings, setListings] = useState([]);
  const [motorcycles, setMotorcycles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [fetching, setFetching] = useState(true);

  const load = async () => {
    setFetching(true);
    try {
      const [l, m, b] = await Promise.all([
        api.get("/listings/host/me"),
        api.get("/motorcycles/host/me"),
        api.get("/bookings/host"),
      ]);
      setListings(l.data);
      setMotorcycles(m.data);
      setBookings(b.data);
    } finally { setFetching(false); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const onDelete = async (id) => {
    if (!confirm("Remove this listing from the marketplace?")) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Listing removed.");
      load();
    } catch { toast.error("Could not delete."); }
  };

  const onDeleteMoto = async (id) => {
    if (!confirm("Remove this motorcycle from the marketplace?")) return;
    try {
      await api.delete(`/motorcycles/${id}`);
      toast.success("Motorcycle removed.");
      load();
    } catch { toast.error("Could not delete."); }
  };

  if (loading) {
    return <div className="min-h-screen"><Navbar />
      <div className="max-w-7xl mx-auto px-6 py-24 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
    </div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen"><Navbar />
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-24 pb-32 text-center" data-testid="auth-gate">
          <div className="overline">Host portal</div>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl tracking-tight">Sign in to list your home.</h1>
          <p className="mt-5 text-sm max-w-xl mx-auto" style={{ color: "var(--text-2)" }}>
            HummingStayz is invite-light. Continue with Google to upload a property, manage bookings and add curated extras.
          </p>
          <button onClick={startGoogleLogin} className="btn-pill btn-primary mt-8" data-testid="signin-google-btn">
            Continue with Google
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="overline">Host dashboard</div>
            <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl tracking-tight">Welcome, {user.name?.split(" ")[0]}.</h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => nav("/dashboard/new")} className="btn-pill btn-primary" data-testid="new-listing-btn">
              <Plus className="w-4 h-4" /> Add new home
            </button>
            <button onClick={() => nav("/dashboard/motorcycles/new")} className="btn-pill btn-outline-dark" data-testid="new-moto-btn">
              <Bike className="w-4 h-4" /> Add motorcycle
            </button>
          </div>
        </div>

        {/* Listings */}
        <section className="mb-16" data-testid="my-listings-section">
          <h2 className="font-serif-display text-3xl mb-6">Your properties</h2>
          {fetching ? (
            <div className="text-sm" style={{ color: "var(--text-2)" }}>Loading…</div>
          ) : listings.length === 0 ? (
            <div className="surface-card p-10 text-center" data-testid="no-listings">
              <p className="font-serif-display text-2xl">No listings yet.</p>
              <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>Add your first long-stay home in under 2 minutes.</p>
              <button onClick={() => nav("/dashboard/new")} className="btn-pill btn-primary mt-6 inline-flex">
                <Plus className="w-4 h-4" /> Add listing
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((l) => {
                const cover = l.images?.[0];
                const src = cover ? (cover.startsWith("http") ? cover : imageUrl(cover)) : null;
                return (
                  <div key={l.id} className="surface-card overflow-hidden" data-testid={`host-listing-${l.id}`}>
                    {src ? <img src={src} alt="" className="w-full h-44 object-cover" /> :
                      <div className="w-full h-44" style={{ background: "var(--surface-alt)" }} />}
                    <div className="p-5">
                      <div className="font-medium">{l.title}</div>
                      <div className="text-xs" style={{ color: "var(--text-2)" }}>{l.area}, {l.city}</div>
                      <div className="mt-2 text-sm text-[color:var(--forest)]">{fmtINR(l.price_monthly)} / month</div>
                      <div className="flex gap-2 mt-5">
                        <Link to={`/dashboard/edit/${l.id}`} className="btn-pill btn-outline-dark !py-2 !px-4 text-xs" data-testid={`edit-listing-${l.id}`}>
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </Link>
                        <Link to={`/listings/${l.id}`} className="btn-pill btn-outline-dark !py-2 !px-4 text-xs">
                          View
                        </Link>
                        <button onClick={() => onDelete(l.id)} className="btn-pill !py-2 !px-4 text-xs" data-testid={`delete-listing-${l.id}`}
                          style={{ background: "transparent", border: "1px solid #9E4747", color: "#9E4747" }}>
                          <Trash2 className="w-3.5 h-3.5" /> Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Motorcycles */}
        <section className="mb-16" data-testid="my-moto-section">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif-display text-3xl">Your motorcycles</h2>
            <button onClick={() => nav("/dashboard/motorcycles/new")} className="btn-pill btn-outline-dark !py-2 !px-4 text-xs" data-testid="add-moto-inline-btn">
              <Plus className="w-3.5 h-3.5" /> Add bike
            </button>
          </div>
          {fetching ? (
            <div className="text-sm" style={{ color: "var(--text-2)" }}>Loading…</div>
          ) : motorcycles.length === 0 ? (
            <div className="surface-card p-10 text-center" data-testid="no-motorcycles">
              <Bike className="w-6 h-6 mx-auto" style={{ color: "var(--terracotta)" }} />
              <p className="font-serif-display text-2xl mt-3">No motorcycles yet.</p>
              <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>List a bike independently — guests can add it to any stay during booking.</p>
              <button onClick={() => nav("/dashboard/motorcycles/new")} className="btn-pill btn-primary mt-6 inline-flex">
                <Plus className="w-4 h-4" /> Add motorcycle
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {motorcycles.map((m) => {
                const cover = m.photos?.[0];
                const src = cover ? (cover.startsWith("http") ? cover : imageUrl(cover)) : null;
                return (
                  <div key={m.id} className="surface-card overflow-hidden" data-testid={`host-moto-${m.id}`}>
                    {src ? <img src={src} alt="" className="w-full h-44 object-cover" /> :
                      <div className="w-full h-44" style={{ background: "var(--surface-alt)" }} />}
                    <div className="p-5">
                      <div className="font-medium flex items-center gap-2">
                        <Bike className="w-4 h-4" style={{ color: "var(--terracotta)" }} /> {m.name}
                      </div>
                      <div className="text-xs" style={{ color: "var(--text-2)" }}>Pickup: {m.pickup_location}</div>
                      <div className="mt-2 text-sm text-[color:var(--forest)]">{fmtINR(m.price_per_day)} / day</div>
                      <div className="flex gap-2 mt-5">
                        <Link to={`/dashboard/motorcycles/edit/${m.id}`} className="btn-pill btn-outline-dark !py-2 !px-4 text-xs" data-testid={`edit-moto-${m.id}`}>
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </Link>
                        <button onClick={() => onDeleteMoto(m.id)} className="btn-pill !py-2 !px-4 text-xs" data-testid={`delete-moto-${m.id}`}
                          style={{ background: "transparent", border: "1px solid #9E4747", color: "#9E4747" }}>
                          <Trash2 className="w-3.5 h-3.5" /> Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Bookings */}
        <section data-testid="host-bookings-section">
          <h2 className="font-serif-display text-3xl mb-6">Bookings on your stays</h2>
          {bookings.length === 0 ? (
            <div className="surface-card p-8 flex items-center gap-3" style={{ color: "var(--text-2)" }}>
              <BookOpen className="w-5 h-5" /> No bookings yet.
            </div>
          ) : (
            <div className="surface-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left" style={{ background: "var(--surface-alt)" }}>
                    <th className="p-4 overline">Listing</th>
                    <th className="p-4 overline">Guest</th>
                    <th className="p-4 overline">Dates</th>
                    <th className="p-4 overline">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-t" style={{ borderColor: "var(--line)" }} data-testid={`booking-row-${b.id}`}>
                      <td className="p-4">{b.listing_title}</td>
                      <td className="p-4">
                        <div>{b.guest_name}</div>
                        <div className="text-xs" style={{ color: "var(--text-2)" }}>{b.guest_email}</div>
                      </td>
                      <td className="p-4 text-xs">{b.check_in} → {b.check_out} <span className="text-[color:var(--text-3)]">({b.nights}n)</span></td>
                      <td className="p-4 font-medium text-[color:var(--forest)]">{fmtINR(b.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
